import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CircularProgress } from "@mui/material";
import Modal from "react-modal";
import Select, {
  components,
  type MultiValue,
  type OptionProps,
  type StylesConfig,
} from "react-select";

import type {
  PathwayCohortStatsResponse,
  PathwayFacetsResponse,
  PathwayPatientRows,
} from "@thalamos/aspire-common";
import type { MhaStatusRow, PathwayMatch } from "@thalamos/pathway-logic";

import { api } from "../../../api.js";
import { isLondonTrust } from "../data/londonTrusts.js";
import type { MhaStatusData } from "../data/mhaStatusData.js";
import { buildWindowedData, reviveMatch } from "../data/pathwayDataSource.js";
import type { CurveType } from "../stats/curveFitting.js";
import { PathwayAnalysis } from "./PathwayAnalysis.js";
import { PatientPathwaySidebar } from "./PatientPathwaySidebar.js";
import { Scatterplot } from "./ScatterPlot.js";

// react-modal needs the app root registered so it can hide background content
// from assistive tech while a modal is open (and to silence its dev warning).
// Guarded for environments without #root (unit tests / storybook).
if (typeof document !== "undefined" && document.getElementById("root")) {
  Modal.setAppElement("#root");
}

type SelectOption = { value: string; label: string };

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Build the zoomed single-patient view: the same data, but with the x-axis range
 * tightened to just this patient's events (+ padding) so their timeline fills the
 * graph instead of being squashed into the whole-cohort axis. Uses the event
 * circles (real event timestamps); the line trails to the cohort end, so it isn't
 * a reliable extent.
 */
function makeFocusView(
  data: MhaStatusData,
  patient: MhaStatusData["patients"][number],
): MhaStatusData {
  const xs = patient.circles.map((c) => c.x).filter((x) => Number.isFinite(x));
  // The death marker is a separate vertical line, not a circle — include it so
  // the zoom window doesn't clip it off.
  if (patient.deceasedDateTime) {
    xs.push(patient.deceasedDateTime.getTime());
  }
  let dateRange = data.dateRange;
  if (xs.length > 0) {
    const min = Math.min(...xs);
    const max = Math.max(...xs);
    const pad = Math.max((max - min) * 0.05, 7 * DAY_MS);
    dateRange = [new Date(min - pad), new Date(max + pad)];
  }
  return { ...data, patients: [patient], dateRange };
}

const selectStyles: StylesConfig<SelectOption, true> = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    fontSize: 12,
    borderColor: "#ccc",
    "&:hover": { borderColor: "#999" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 6px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 32,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#e0e7ff",
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontSize: 11,
    padding: 2,
  }),
  multiValueRemove: (base) => ({
    ...base,
    "&:hover": {
      backgroundColor: "#c7d2fe",
      color: "#4338ca",
    },
  }),
  menu: (base) => ({
    ...base,
    fontSize: 12,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#e0e7ff"
      : state.isFocused
        ? "#f3f4f6"
        : "white",
    color: state.isSelected ? "#4338ca" : "#111827",
    fontWeight: state.isSelected ? 500 : 400,
    "&:active": { backgroundColor: "#c7d2fe" },
  }),
};

const CustomOption = (props: OptionProps<SelectOption, true>) => {
  return (
    <components.Option {...props}>
      <span
        style={{
          marginRight: 8,
          color: "#4f46e5",
          fontWeight: 600,
          visibility: props.isSelected ? "visible" : "hidden",
        }}
      >
        ✓
      </span>
      {props.children}
    </components.Option>
  );
};

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
  },
};

// Parse URL params for initial state
function getUrlParams() {
  const params = new URLSearchParams(window.location.search);
  const hospitalsParam = params.get("hospitals");
  const trustsParam = params.get("trusts");
  return {
    hospitals: hospitalsParam
      ? hospitalsParam
          .split(",")
          .filter(Boolean)
          .map((h) => ({ value: h, label: h }))
      : [],
    trusts: trustsParam
      ? trustsParam
          .split(",")
          .filter(Boolean)
          .map((t) => ({ value: t, label: t }))
      : [],
    crossMhTrust: params.get("crossMhTrust") === "true", // default false
    acuteToMh: params.get("acuteToMh") === "true",
    londonTrusts: params.get("londonTrusts") !== "false", // default true
    zoomPatient: params.get("zoom") || null,
    pathwayDsl: params.get("pathwayDsl") || null,
    applyPathwayFilter: params.get("applyPathway") === "true",
    measurementModal: params.get("measure") || null,
    measurementCurve: params.get("curve") || null,
  };
}

// Update URL without reload - always include all params
function updateUrl(
  state: {
    hospitals: SelectOption[];
    trusts: SelectOption[];
    crossMhTrust: boolean;
    acuteToMh: boolean;
    londonTrusts: boolean;
    zoomPatient: string | null;
    pathwayDsl: string | null;
    applyPathwayFilter: boolean;
    measurementModal?: string | null;
    measurementCurve?: string | null;
  },
  usePushState = true,
) {
  const params = new URLSearchParams();
  params.set("hospitals", state.hospitals.map((h) => h.value).join(","));
  params.set("trusts", state.trusts.map((t) => t.value).join(","));
  params.set("crossMhTrust", state.crossMhTrust.toString());
  params.set("acuteToMh", state.acuteToMh.toString());
  params.set("londonTrusts", state.londonTrusts.toString());
  if (state.zoomPatient) {
    params.set("zoom", state.zoomPatient);
  }
  if (state.pathwayDsl) {
    params.set("pathwayDsl", state.pathwayDsl);
  }
  params.set("applyPathway", state.applyPathwayFilter.toString());
  if (state.measurementModal) {
    params.set("measure", state.measurementModal);
  }
  if (state.measurementCurve && state.measurementCurve !== "none") {
    params.set("curve", state.measurementCurve);
  }

  const newUrl = `${window.location.pathname}?${params.toString()}`;

  // Only push if URL actually changed
  if (newUrl !== window.location.pathname + window.location.search) {
    if (usePushState) {
      window.history.pushState(state, "", newUrl);
    } else {
      window.history.replaceState(state, "", newUrl);
    }
  }
}

// Sidebar content component that handles both normal and patient-focused views
const SidebarContent: React.FC<{
  data: MhaStatusData;
  focusPatient: MhaStatusData | null;
  zoomPatientId: string | null;
  pathwayDsl: string | null;
  setPathwayDsl: (dsl: string | null) => void;
  applyPathwayFilter: boolean;
  setApplyPathwayFilter: (apply: boolean) => void;
  reload: (clearZoom?: boolean, overridePathwayDsl?: string | null) => void;
  onClose?: () => void;
  measurementModal: string | null;
  setMeasurementModal: (measurement: string | null) => void;
  measurementCurve: string | null;
  setMeasurementCurve: (curve: string | null) => void;
  serverMatches: PathwayMatch[];
  serverMatchedCount: number;
  serverTotalPatients: number;
  onPreviewDslChange: (dsl: string | null) => void;
  previewMatches: PathwayMatch[] | null;
  previewMatchedCount: number | undefined;
  previewLoading: boolean;
}> = ({
  data,
  focusPatient,
  zoomPatientId,
  pathwayDsl,
  setPathwayDsl,
  applyPathwayFilter,
  setApplyPathwayFilter,
  reload,
  onClose,
  measurementModal,
  setMeasurementModal,
  measurementCurve,
  setMeasurementCurve,
  serverMatches,
  serverMatchedCount,
  serverTotalPatients,
  onPreviewDslChange,
  previewMatches,
  previewMatchedCount,
  previewLoading,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    void navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    });
  };
  // Match stats come from the server (whole-cohort), not the loaded window.
  const allPatientsMatchData = useMemo(
    () => ({ matches: serverMatches, patientsMatched: serverMatchedCount }),
    [serverMatches, serverMatchedCount],
  );

  // Get detention periods for focused patient
  const focusedPatientData = useMemo(() => {
    if (!zoomPatientId) return null;
    const patient = data.allFilteredPatients.find(
      (p) => p.id === zoomPatientId,
    );
    return patient || null;
  }, [zoomPatientId, data.allFilteredPatients]);

  return (
    <div
      style={{
        position: "fixed",
        right: 0,
        top: 0,
        width: 180,
        height: "100vh",
        padding: 10,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        background: "white",
        borderLeft: "1px solid #e5e7eb",
        overflowY: "auto",
        zIndex: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {focusPatient ? (
          <>
            <span
              style={{ fontSize: "14px", color: "#6b7280", marginBottom: 8 }}
            >
              Patient View
            </span>
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <button
                onClick={onClose}
                style={{
                  padding: "6px 12px",
                  fontSize: 11,
                  background: "#f3f4f6",
                  border: "1px solid #d1d5db",
                  borderRadius: 4,
                  cursor: "pointer",
                  color: "#374151",
                  fontWeight: 500,
                }}
              >
                ← Back to Grid
              </button>
              <button
                onClick={handleCopyLink}
                style={{
                  padding: "6px 12px",
                  fontSize: 11,
                  background: copied ? "#10b981" : "#4f46e5",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  color: "white",
                  fontWeight: 500,
                  transition: "background 0.2s",
                }}
              >
                {copied ? "✓ Copied!" : "🔗 Copy Link"}
              </button>
              {focusedPatientData && (
                <button
                  onClick={() => {
                    const debugData = {
                      patientId: focusedPatientData.id,
                      entries: focusedPatientData.entries,
                      detentionPeriods: focusedPatientData.detentionPeriods,
                    };
                    const blob = new Blob(
                      [JSON.stringify(debugData, null, 2)],
                      { type: "application/json" },
                    );
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `patient-${focusedPatientData.id}-debug.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  style={{
                    padding: "6px 12px",
                    fontSize: 11,
                    background: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRadius: 4,
                    cursor: "pointer",
                    color: "#374151",
                    fontWeight: 500,
                  }}
                  title="Download raw events and detention periods for debugging"
                >
                  ⬇ Debug
                </button>
              )}
            </div>
          </>
        ) : null}
      </div>

      {focusPatient && focusedPatientData ? (
        <PatientPathwaySidebar
          patientId={zoomPatientId || ""}
          detentionPeriods={focusedPatientData.detentionPeriods}
          pathwayDsl={pathwayDsl}
          allPatientsMatches={allPatientsMatchData.matches}
          totalPatientsMatched={allPatientsMatchData.patientsMatched}
        />
      ) : (
        (() => {
          return (
            <PathwayAnalysis
              patients={data.allFilteredPatients}
              serverMatches={serverMatches}
              serverMatchedCount={serverMatchedCount}
              serverTotalPatients={serverTotalPatients}
              onPreviewDslChange={onPreviewDslChange}
              previewMatches={previewMatches}
              previewMatchedCount={previewMatchedCount}
              previewLoading={previewLoading}
              pathwayDsl={pathwayDsl ?? undefined}
              onPathwayDslChange={setPathwayDsl}
              applyToGrid={applyPathwayFilter}
              onApplyToGridChange={(apply, dslText) => {
                setApplyPathwayFilter(apply);
                setPathwayDsl(dslText);
                reload(false, apply ? dslText : null);
              }}
              onApplyFilter={(dslText) => {
                setPathwayDsl(dslText);
                reload(false, dslText);
              }}
              expandedMeasurement={measurementModal}
              onExpandedMeasurementChange={setMeasurementModal}
              measurementCurve={(measurementCurve as CurveType) ?? undefined}
              onMeasurementCurveChange={(curve) => {
                setMeasurementCurve(curve);
              }}
            />
          );
        })()
      )}
    </div>
  );
};

export const Demo = ({
  width = 700,
  height = 400,
}: {
  width: number;
  height: number;
}) => {
  const initialParams = getUrlParams();

  // Server-backed data source (replaces the CSV upload + web worker): cohort
  // facets load once; the filtered raw rows are re-fetched when a server-side
  // filter changes; `data` is then derived on the main thread by
  // buildWindowedData for just the visible window (see the effects below).
  const [facets, setFacets] = useState<PathwayFacetsResponse | null>(null);
  // Whole-cohort aggregates from the server (ordered grid ids + DSL matches).
  const [cohortStats, setCohortStats] =
    useState<PathwayCohortStatsResponse | null>(null);
  // Paged timelines: only the patients the grid has actually requested.
  const [timelinesById, setTimelinesById] = useState<
    Map<string, PathwayPatientRows>
  >(() => new Map());
  const [error, setError] = useState<string | null>(null);
  const [focusPatient, setFocusPatient] = useState<MhaStatusData | null>(null);
  const [zoomPatientId, setZoomPatientId] = useState<string | null>(
    initialParams.zoomPatient,
  );
  const [loading, setLoading] = useState(false);
  const [londonTrustsSelected, setLondonTrustsSelected] = useState(
    initialParams.londonTrusts,
  );

  // Lazy loading state - will be set based on cols (10 rows max)
  const [visibleCount, setVisibleCount] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Grid scroll position, preserved across opening/closing a zoomed patient.
  const savedScrollTop = useRef(0);
  // The sticky filter bar's real height. It runs taller than the HEADER_HEIGHT
  // guess (labels + two-row checkboxes), and the focus chart is absolutely
  // positioned below it — so measure it rather than hard-code, otherwise the
  // chart's top strip (now the trust ribbon) hides behind the bar.
  const filterBarRef = useRef<HTMLDivElement>(null);
  const [filterBarHeight, setFilterBarHeight] = useState(64);

  const [mouseX, setMouseX] = useState<number | null>(null);
  const [highlightedJson, setHighlightedJson] = useState<MhaStatusRow | null>(
    null,
  );
  const [selectedHospitals, setSelectedHospitals] = useState<SelectOption[]>(
    initialParams.hospitals,
  );
  const [selectedTrusts, setSelectedTrusts] = useState<SelectOption[]>(
    initialParams.trusts,
  );
  const [crossMhTrust, setCrossMhTrust] = useState(initialParams.crossMhTrust);
  const [acuteToMh, setAcuteToMh] = useState(initialParams.acuteToMh);

  // Pathway filter state
  const [pathwayDsl, setPathwayDsl] = useState<string | null>(
    initialParams.pathwayDsl,
  );
  const [applyPathwayFilter, setApplyPathwayFilter] = useState(
    initialParams.applyPathwayFilter,
  );

  // Live DSL preview: while the editor modal is open, the current textarea DSL is
  // pushed here and matched server-side WITHOUT applying it to the grid, so the
  // modal's count + measurement distributions track what's being typed. Null when
  // the modal is closed (falls back to the applied matches). See PathwayAnalysis.
  const [previewDsl, setPreviewDsl] = useState<string | null>(null);
  const [previewStats, setPreviewStats] =
    useState<PathwayCohortStatsResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Measurement modal state (for URL sharing)
  const [measurementModal, setMeasurementModal] = useState<string | null>(
    initialParams.measurementModal,
  );
  const [measurementCurve, setMeasurementCurve] = useState<string | null>(
    initialParams.measurementCurve,
  );

  // Render geometry is built ONLY for the loaded window (paging) — never the
  // whole cohort. The DSL match/stat work already happened server-side.
  const data = useMemo<MhaStatusData | null>(
    () =>
      facets && cohortStats
        ? buildWindowedData(
            facets,
            cohortStats.patientIds,
            timelinesById,
            cohortStats.totalPatients,
          )
        : null,
    [facets, cohortStats, timelinesById],
  );
  // Lean server matches revived into the shape the stats views expect.
  const serverMatches = useMemo(
    () => (cohortStats?.matches ?? []).map(reviveMatch),
    [cohortStats],
  );
  // Preview matches (the unapplied editor DSL) revived the same way; null when
  // there's no active preview, so the modal falls back to the applied matches.
  const previewMatches = useMemo(
    () => (previewStats ? previewStats.matches.map(reviveMatch) : null),
    [previewStats],
  );

  // Whether the loaded cohort contains any London trust at all — drives the
  // "London Trusts" quick-filter's availability (dev cohorts have none).
  const hasLondonTrusts = useMemo(
    () => facets?.trusts.some((t) => isLondonTrust(t.name)) ?? false,
    [facets],
  );

  // Track if we're handling a popstate event to avoid pushing state
  const isHandlingPopstate = useRef(false);
  // Track first render to use replaceState instead of pushState
  const isFirstRender = useRef(true);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopstate = () => {
      isHandlingPopstate.current = true;
      const params = getUrlParams();
      setSelectedHospitals(params.hospitals);
      setSelectedTrusts(params.trusts);
      setCrossMhTrust(params.crossMhTrust);
      setAcuteToMh(params.acuteToMh);
      setLondonTrustsSelected(params.londonTrusts);
      setZoomPatientId(params.zoomPatient);
      setPathwayDsl(params.pathwayDsl);
      setApplyPathwayFilter(params.applyPathwayFilter);
      setMeasurementModal(params.measurementModal);
      setMeasurementCurve(params.measurementCurve);
      // Reset after a tick to allow state updates to complete
      setTimeout(() => {
        isHandlingPopstate.current = false;
      }, 0);
    };

    window.addEventListener("popstate", handlePopstate);
    return () => {
      window.removeEventListener("popstate", handlePopstate);
    };
  }, []);

  // Update URL when filters change
  useEffect(() => {
    // Don't push state if we're handling a popstate event
    if (isHandlingPopstate.current) return;

    const state = {
      hospitals: selectedHospitals,
      trusts: selectedTrusts,
      crossMhTrust,
      acuteToMh,
      londonTrusts: londonTrustsSelected,
      zoomPatient: zoomPatientId,
      pathwayDsl,
      applyPathwayFilter,
      measurementModal,
      measurementCurve,
    };

    // Use replaceState for first render, pushState for subsequent changes
    updateUrl(state, !isFirstRender.current);
    isFirstRender.current = false;
  }, [
    selectedHospitals,
    selectedTrusts,
    crossMhTrust,
    acuteToMh,
    londonTrustsSelected,
    zoomPatientId,
    pathwayDsl,
    applyPathwayFilter,
    measurementModal,
    measurementCurve,
  ]);

  // Default the trust filter to the London trusts once, when the cohort's trust
  // list loads. Keyed off `facets` (stable), NOT `data` — `data` gets a new
  // identity on every timeline page-load, and setting a fresh empty array here
  // when no London trust is present flipped `selectedTrusts`, which re-triggered
  // the cohort-stats fetch: an infinite loop in any cohort without a London
  // trust. Only assign when there is actually a match.
  useEffect(() => {
    if (!facets || !londonTrustsSelected || selectedTrusts.length > 0) return;
    const londonTrustsInData = facets.trusts
      .filter((t) => isLondonTrust(t.name))
      .map((t) => ({ value: t.name, label: t.name }));
    if (londonTrustsInData.length > 0) {
      setSelectedTrusts(londonTrustsInData);
    } else {
      // No London trust in this cohort (e.g. dev): clear the default so the
      // toggle isn't misleadingly active and the grid shows the full cohort.
      setLondonTrustsSelected(false);
    }
  }, [facets, londonTrustsSelected, selectedTrusts.length]);

  // Restore zoom state when data loads
  useEffect(() => {
    if (data && zoomPatientId) {
      const patient = data.patients.find((p) => p.id === zoomPatientId);
      if (patient) {
        setFocusPatient(makeFocusView(data, patient));
      }
    }
  }, [data, zoomPatientId]);

  // ESC key closes zoomed patient view
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && focusPatient) {
        setFocusPatient(null);
        setZoomPatientId(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [focusPatient]);

  // Returning from a zoomed patient to the grid: restore the scroll position
  // (opening the focus view shrinks the content, which resets scrollTop to 0).
  useLayoutEffect(() => {
    if (!focusPatient && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = savedScrollTop.current;
    }
  }, [focusPatient]);

  // Keep the focus chart clear of the sticky filter bar by measuring its real
  // height. It changes as the filter controls populate (facets load) and wrap
  // (width), so a ResizeObserver re-measures; the functional guard only
  // re-renders on an actual change (the setter is a stable dep, so [] is honest).
  useLayoutEffect(() => {
    const el = filterBarRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      setFilterBarHeight((prev) => (Math.abs(h - prev) > 1 ? h : prev));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  // Apply a pathway filter / clear the zoomed view. Filter + DSL state changes
  // trigger the cohort-stats re-fetch below; `data` then re-derives from its memo.
  const reload = useCallback(
    (clearZoom = false, overridePathwayDsl?: string | null) => {
      setMouseX(null);
      setFocusPatient(null);
      if (clearZoom) {
        setZoomPatientId(null);
      }
      if (overridePathwayDsl !== undefined) {
        setPathwayDsl(overridePathwayDsl);
        setApplyPathwayFilter(overridePathwayDsl !== null);
      }
    },
    [],
  );

  // Cohort facets load once — the hospital/trust lists + chart date range.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.pathway.getFacets();
        if (cancelled) return;
        if (res.status === 200) {
          setFacets(res.data);
        } else {
          setError("Could not load pathway facets.");
        }
      } catch {
        if (!cancelled) setError("Could not load pathway facets.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Whole-cohort query: the server derives periods + runs the DSL and returns
  // aggregates + the ordered grid ids (no timelines). Re-runs on any server-side
  // filter or the pathway DSL; resets the paged timelines + the visible window.
  useEffect(() => {
    if (!facets) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const res = await api.pathway.getCohortStats({
          hospitals: selectedHospitals.map((h) => h.value),
          trusts: selectedTrusts.map((t) => t.value),
          crossMhTrust,
          acuteToMh,
          dsl: pathwayDsl,
          applyPathwayFilter,
        });
        if (cancelled) return;
        if (res.status === 200) {
          setCohortStats(res.data);
          setTimelinesById(new Map());
          setVisibleCount(null);
          setError(null);
        } else {
          setError("Could not load pathway cohort.");
        }
      } catch {
        if (!cancelled) setError("Could not load pathway cohort.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    facets,
    selectedHospitals,
    selectedTrusts,
    crossMhTrust,
    acuteToMh,
    pathwayDsl,
    applyPathwayFilter,
  ]);

  // Debounced live-preview query: match the current editor DSL over the SAME base
  // filters as the grid but WITHOUT applying it (applyPathwayFilter:false), so the
  // grid's patient set is untouched while the modal shows live counts. Keyed off
  // the base filters too, so the preview tracks filter changes made with the modal
  // open. previewDsl is null whenever the modal is closed, which clears the stats.
  useEffect(() => {
    if (previewDsl === null) {
      setPreviewStats(null);
      setPreviewLoading(false);
      return;
    }
    setPreviewLoading(true);
    let cancelled = false;
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const res = await api.pathway.getCohortStats({
            hospitals: selectedHospitals.map((h) => h.value),
            trusts: selectedTrusts.map((t) => t.value),
            crossMhTrust,
            acuteToMh,
            dsl: previewDsl,
            applyPathwayFilter: false,
          });
          if (cancelled) return;
          if (res.status === 200) setPreviewStats(res.data);
        } catch {
          // Preview is best-effort; keep the last good numbers on failure.
        } finally {
          if (!cancelled) setPreviewLoading(false);
        }
      })();
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [previewDsl, selectedHospitals, selectedTrusts, crossMhTrust, acuteToMh]);

  // Page timelines: fetch raw rows for the visible window's patients that aren't
  // loaded yet and merge them in — this is what stops the whole cohort loading.
  useEffect(() => {
    if (!cohortStats || visibleCount === null) return;
    const windowIds = cohortStats.patientIds.slice(0, visibleCount);
    const missing = windowIds.filter((id) => !timelinesById.has(id));
    if (missing.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.pathway.getPatientTimelines(missing);
        if (cancelled || res.status !== 200) return;
        setTimelinesById((prev) => {
          const next = new Map(prev);
          for (const patient of res.data.patients) {
            next.set(patient.rootPatientId, patient);
          }
          return next;
        });
      } catch {
        // Leave the window partially rendered; a later scroll retries.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cohortStats, visibleCount, timelinesById]);

  // Ensure a zoomed patient's timeline is loaded even when it's past the paged
  // window (e.g. restored from a shared URL, or a match beyond the first page):
  // fetch it on demand so it becomes present in `data` and can be focused.
  useEffect(() => {
    if (!cohortStats || !zoomPatientId) return;
    if (timelinesById.has(zoomPatientId)) return;
    if (!cohortStats.patientIds.includes(zoomPatientId)) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.pathway.getPatientTimelines([zoomPatientId]);
        if (cancelled || res.status !== 200) return;
        setTimelinesById((prev) => {
          const next = new Map(prev);
          for (const patient of res.data.patients) {
            next.set(patient.rootPatientId, patient);
          }
          return next;
        });
      } catch {
        // ignore; the patient just won't zoom
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cohortStats, zoomPatientId, timelinesById]);

  // Calculate grid layout based on available space
  const SIDEBAR_WIDTH = 180;
  const HEADER_HEIGHT = 50; // Approximate header height
  const gridAreaWidth = width - SIDEBAR_WIDTH;
  const availableWidth = gridAreaWidth - 50; // 50 for left axis
  const availableHeight = height - HEADER_HEIGHT;

  // Max 10 columns, 10 rows - graphs fill available space
  const MAX_COLS = 10;
  const MAX_ROWS = 10;
  const cols = Math.min(MAX_COLS, Math.max(1, Math.floor(availableWidth / 70)));
  const GRAPH_WIDTH = Math.floor(availableWidth / cols);
  const GRAPH_HEIGHT = Math.floor(availableHeight / MAX_ROWS);
  const maxInitialPatients = cols * MAX_ROWS;
  // Total patients available for the grid (server-ordered); the grid pages
  // timelines up to this, rendering only what's loaded.
  const totalPatients = cohortStats?.patientIds.length ?? 0;

  // Set initial visibleCount based on cols (10 rows)
  useEffect(() => {
    if (visibleCount === null) {
      setVisibleCount(maxInitialPatients);
    }
  }, [visibleCount, maxInitialPatients]);

  // Lazy loading scroll handler - load more when scrolled near the last rendered row
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || visibleCount === null) return;

    const { scrollTop, clientHeight } = scrollContainerRef.current;
    const viewBottom = scrollTop + clientHeight;

    // Calculate where the last rendered patient ends (header is ~50px, add buffer)
    const renderedRows = Math.ceil(visibleCount / Math.max(1, cols));
    const renderedContentBottom = renderedRows * GRAPH_HEIGHT + 60;

    // Load more (10 rows at a time) when within 200px of the rendered content
    // bottom — bumping visibleCount pages in the next window's timelines.
    if (
      viewBottom > renderedContentBottom - 200 &&
      visibleCount < totalPatients
    ) {
      setVisibleCount((prev) =>
        Math.min((prev ?? 0) + cols * MAX_ROWS, totalPatients),
      );
    }
  }, [totalPatients, visibleCount, cols, GRAPH_HEIGHT, MAX_ROWS]);

  if (error) {
    return (
      <div style={{ padding: 24, color: "#b91c1c", fontSize: 14 }}>{error}</div>
    );
  }
  if (!data) {
    return (
      <div style={{ padding: 24, color: "#6b7280", fontSize: 14 }}>
        Loading pathway data…
      </div>
    );
  }

  return (
    <>
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{
          height: "100vh",
          width: `calc(100vw - ${String(SIDEBAR_WIDTH)}px)`,
          overflow: "auto",
          position: "relative",
        }}
      >
        <div
          ref={filterBarRef}
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "white",
            width: gridAreaWidth,
            padding: "8px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e5e7eb",
            boxSizing: "border-box",
          }}
        >
          {highlightedJson ? (
            <Modal
              isOpen={!!highlightedJson}
              onRequestClose={() => {
                setHighlightedJson(null);
              }}
              style={customStyles}
              contentLabel="Example Modal"
            >
              <pre
                style={{
                  margin: 0,
                  fontSize: 12,
                  maxHeight: "70vh",
                  overflow: "auto",
                  background: "#f9fafb",
                  padding: 12,
                  borderRadius: 6,
                }}
              >
                {JSON.stringify(highlightedJson, null, 2)}
              </pre>
            </Modal>
          ) : (
            <></>
          )}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              alignItems: "flex-end",
            }}
          >
            <div style={{ minWidth: 280 }}>
              <span
                style={{ fontSize: "12px", display: "block", marginBottom: 2 }}
              >
                Hospitals:
              </span>
              <Select<SelectOption, true>
                isMulti
                isSearchable
                isClearable
                hideSelectedOptions={false}
                placeholder="All hospitals..."
                options={
                  data?.hospitals.map((h) => ({
                    value: h.name,
                    label: h.name,
                  })) ?? []
                }
                value={selectedHospitals}
                onChange={(newValue: MultiValue<SelectOption>) => {
                  setSelectedHospitals([...newValue]);
                }}
                styles={selectStyles}
                components={{ Option: CustomOption }}
              />
            </div>

            <div style={{ minWidth: 280 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 2,
                }}
              >
                <span style={{ fontSize: "12px" }}>Trusts:</span>
                <button
                  disabled={!hasLondonTrusts}
                  title={
                    hasLondonTrusts
                      ? "Filter to London trusts"
                      : "No London trusts in this cohort"
                  }
                  onClick={() => {
                    const londonTrustsInData =
                      data?.trusts
                        .filter((t) => isLondonTrust(t.name))
                        .map((t) => ({ value: t.name, label: t.name })) ?? [];
                    setSelectedTrusts(londonTrustsInData);
                    setLondonTrustsSelected(true);
                  }}
                  style={{
                    fontSize: 10,
                    padding: "2px 6px",
                    background: !hasLondonTrusts
                      ? "#e5e7eb"
                      : londonTrustsSelected
                        ? "#4f46e5"
                        : "#9ca3af",
                    color: hasLondonTrusts ? "white" : "#9ca3af",
                    border: "none",
                    borderRadius: 3,
                    cursor: hasLondonTrusts ? "pointer" : "not-allowed",
                  }}
                >
                  London Trusts
                </button>
              </div>
              <Select<SelectOption, true>
                isMulti
                isSearchable
                isClearable
                hideSelectedOptions={false}
                options={
                  data?.trusts.map((t) => ({
                    value: t.name,
                    label: t.name,
                  })) ?? []
                }
                value={selectedTrusts}
                onChange={(newValue: MultiValue<SelectOption>) => {
                  setSelectedTrusts([...newValue]);
                  setLondonTrustsSelected(false);
                }}
                styles={selectStyles}
                components={{ Option: CustomOption }}
                controlShouldRenderValue={selectedTrusts.length <= 1}
                placeholder={
                  selectedTrusts.length > 1
                    ? `${String(selectedTrusts.length)} trusts selected`
                    : "All trusts..."
                }
              />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                fontSize: 11,
              }}
            >
              <label
                style={{ display: "flex", alignItems: "center", gap: 4 }}
                title="Patients who moved between two or more mental-health trusts (a genuine inter-provider boundary crossing)"
              >
                <input
                  type="checkbox"
                  checked={crossMhTrust}
                  onChange={(e) => {
                    setCrossMhTrust(e.target.checked);
                  }}
                />
                Cross-trust (MH↔MH)
              </label>
              <label
                style={{ display: "flex", alignItems: "center", gap: 4 }}
                title="Patients who span more than one trust but only local acute ↔ MH movement (e.g. assessed at an acute trust, admitted to the local MH trust) — not a genuine MH-trust boundary crossing"
              >
                <input
                  type="checkbox"
                  checked={acuteToMh}
                  onChange={(e) => {
                    setAcuteToMh(e.target.checked);
                  }}
                />
                Acute ↔ MH
              </label>
            </div>

            {/* Legend */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginLeft: 20,
                padding: "4px 12px",
                background: "#f8fafc",
                borderRadius: 6,
                border: "1px solid #e2e8f0",
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>
                Sections:
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {[
                  { color: "#94a3b8", label: "None" },
                  { color: "#2dd4bf", label: "5(4)" },
                  { color: "#22d3ee", label: "5(2)" },
                  { color: "#60a5fa", label: "S4" },
                  { color: "#818cf8", label: "S2" },
                  { color: "#c084fc", label: "S3" },
                  { color: "#e879f9", label: "CTO" },
                ].map(({ color, label }) => (
                  <div
                    key={label}
                    style={{ display: "flex", alignItems: "center", gap: 3 }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: color,
                      }}
                    />
                    <span style={{ fontSize: 10, color: "#475569" }}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Assessment lane key. The lane is rules and ticks with no colour
                or shape vocabulary, so all it needs naming is the two marks. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 12px",
                background: "#f8fafc",
                borderRadius: 6,
                border: "1px solid #e2e8f0",
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>
                Assessment:
              </span>
              <svg width={30} height={12} aria-hidden>
                <rect
                  x={0}
                  y={5}
                  width={22}
                  height={2}
                  rx={1}
                  fill="#475569"
                  opacity={0.75}
                />
                <line
                  x1={22}
                  x2={22}
                  y1={1}
                  y2={11}
                  stroke="#475569"
                  strokeWidth={1}
                  opacity={0.9}
                />
              </svg>
              <span style={{ fontSize: 10, color: "#475569" }}>
                span, ending where it resolved (hover for the outcome)
              </span>
            </div>

            {/* Patient count (whole cohort, from the server) */}
            {cohortStats && (
              <div style={{ fontSize: 11, color: "#64748b" }}>
                {applyPathwayFilter && pathwayDsl
                  ? `${String(cohortStats.matchedPatientCount)} / ${String(cohortStats.totalPatients)} patients`
                  : `${String(cohortStats.totalPatients)} patients`}
              </div>
            )}
          </div>
        </div>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              background: "rgba(255,255,255,0.72)",
              zIndex: 20,
            }}
          >
            <CircularProgress size={28} sx={{ color: "#0f4c5c" }} />
            <div style={{ fontSize: 13, color: "#475569" }}>
              Processing pathway data…
            </div>
          </div>
        )}
        {!loading &&
          data &&
          (focusPatient ? (
            <>
              <Scatterplot
                onClick={() => {
                  setFocusPatient(null);
                  setZoomPatientId(null);
                }}
                onCircleClick={(event: MhaStatusRow) => {
                  setHighlightedJson(event);
                }}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.pageX - rect.x - 50;
                  setMouseX(x);
                }}
                highlightX={mouseX}
                showAxisLeft={true}
                showAxisBottom={true}
                showTrustBand={true}
                data={focusPatient}
                width={gridAreaWidth}
                top={filterBarHeight}
                left={0}
                height={height - filterBarHeight}
              />
            </>
          ) : visibleCount === null ? null : (
            (() => {
              // Render up to visibleCount patients (starts at maxInitialPatients, increases on scroll)
              const patientsToRender = data.patients.slice(0, visibleCount);
              const renderedRows = Math.ceil(patientsToRender.length / cols);
              // Only reserve height for rendered patients + a bit extra for loading indicator
              const contentHeight =
                renderedRows * GRAPH_HEIGHT +
                (visibleCount < totalPatients ? 100 : 0);

              return (
                <div style={{ height: contentHeight, position: "relative" }}>
                  {patientsToRender.map((patient, index) => {
                    const row = Math.floor(index / cols);
                    const col = index % cols;
                    const isFirstCol = col === 0;

                    return (
                      <Scatterplot
                        key={patient.id}
                        onClick={() => {
                          // Remember scroll before the focus view collapses it.
                          savedScrollTop.current =
                            scrollContainerRef.current?.scrollTop ?? 0;
                          setFocusPatient(makeFocusView(data, patient));
                          setZoomPatientId(patient.id);
                        }}
                        onCircleClick={(event: MhaStatusRow) => {
                          setHighlightedJson(event);
                        }}
                        onMouseMove={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const hoverX =
                            e.pageX - rect.x - (isFirstCol ? 50 : 0);
                          setMouseX(hoverX);
                        }}
                        highlightX={mouseX}
                        showAxisLeft={isFirstCol}
                        showAxisBottom={false}
                        data={{ ...data, patients: [patient] }}
                        width={isFirstCol ? 50 + GRAPH_WIDTH : GRAPH_WIDTH}
                        top={row * GRAPH_HEIGHT}
                        left={isFirstCol ? 0 : 50 + col * GRAPH_WIDTH}
                        height={GRAPH_HEIGHT}
                      />
                    );
                  })}

                  {/* Loading placeholders for remaining patients */}
                  {visibleCount < totalPatients && (
                    <div
                      style={{
                        position: "absolute",
                        top: Math.ceil(visibleCount / cols) * GRAPH_HEIGHT,
                        left: 50,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0,
                      }}
                    >
                      {Array.from({
                        length: Math.min(
                          cols * 2,
                          totalPatients - visibleCount,
                        ),
                      }).map((_, i) => (
                        <div
                          key={`placeholder-${String(i)}`}
                          className="graph-placeholder"
                          style={{
                            width: GRAPH_WIDTH,
                            height: GRAPH_HEIGHT,
                          }}
                        />
                      ))}
                    </div>
                  )}

                  {/* Date display on hover */}
                  {mouseX !== null && (
                    <div
                      style={{
                        position: "fixed",
                        bottom: 30,
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "rgba(0,0,0,0.7)",
                        color: "white",
                        padding: "4px 12px",
                        borderRadius: 4,
                        fontSize: 12,
                        pointerEvents: "none",
                        zIndex: 100,
                      }}
                    >
                      {new Date(
                        (data.dateRange[1].getTime() -
                          data.dateRange[0].getTime()) *
                          // The pointer reaches the margins either side of the
                          // plot area, where the raw fraction falls outside
                          // [0, 1] and would read out a date off the axis.
                          Math.min(Math.max(mouseX / (GRAPH_WIDTH - 5), 0), 1) +
                          data.dateRange[0].getTime(),
                      ).toDateString()}
                    </div>
                  )}
                </div>
              );
            })()
          ))}
      </div>
      {!loading && data && (
        <SidebarContent
          data={data}
          focusPatient={focusPatient}
          zoomPatientId={zoomPatientId}
          pathwayDsl={pathwayDsl}
          setPathwayDsl={setPathwayDsl}
          applyPathwayFilter={applyPathwayFilter}
          setApplyPathwayFilter={setApplyPathwayFilter}
          reload={reload}
          onClose={() => {
            setFocusPatient(null);
            setZoomPatientId(null);
          }}
          measurementModal={measurementModal}
          setMeasurementModal={setMeasurementModal}
          measurementCurve={measurementCurve}
          setMeasurementCurve={setMeasurementCurve}
          serverMatches={serverMatches}
          serverMatchedCount={cohortStats?.matchedPatientCount ?? 0}
          serverTotalPatients={cohortStats?.totalPatients ?? 0}
          onPreviewDslChange={setPreviewDsl}
          previewMatches={previewMatches}
          previewMatchedCount={previewStats?.matchedPatientCount}
          previewLoading={previewLoading}
        />
      )}
      <div
        style={{
          position: "fixed",
          bottom: 8,
          right: 8,
          fontSize: "11px",
          color: "#999",
        }}
      >
        © Thalamos 2026
      </div>
    </>
  );
};
